'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Calendar, Clock, Users, RefreshCw, MapPin, AlertCircle, CheckCircle, Star, ChevronRight } from 'lucide-react'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { toast } from '@/components/ui/ToastContainer'
import { CardSkeleton } from '@/components/ui/Skeleton'
import { useTheme } from '@/contexts/ThemeContext'
import BuddyReviewModal, { ReviewData } from '@/components/BuddyReviewModal'
import { parseAuthToken } from '@/utils/base64'

interface UserProfile {
  name: string
  profile_image?: string
  certification_level?: string
  is_premium?: boolean
}

interface MatchingRequest {
  id: string
  date: string
  time: string
  location: string
  diving_center: string
  experience_level: string
  status: string
  created_at: string
  matched_user_id?: string
  user_id: string
}

interface BuddyInfo {
  id: string
  name: string
  profile_image?: string
  isWithdrawn?: boolean
}

export default function ReservationsPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const bg = isDark ? '#0f172a' : '#f4f7fb'
  const card = isDark ? '#1e2a3d' : '#ffffff'
  const border = isDark ? '#2c3a52' : '#e9eff7'
  const ink = isDark ? '#e8f0fb' : '#0a1f3a'
  const secondary = isDark ? '#94a3b8' : '#6b7684'
  const accent = isDark ? '#5ea6ff' : '#2b6df0'

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [matchingRequests, setMatchingRequests] = useState<MatchingRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentUserId, setCurrentUserId] = useState<string>('')
  const [reviewModalOpen, setReviewModalOpen] = useState(false)
  const [selectedMatching, setSelectedMatching] = useState<{
    matchingId: string
    buddyId: string
    buddyName: string
  } | null>(null)
  const [reviewedMatchings, setReviewedMatchings] = useState<Set<string>>(new Set())
  const [buddyInfoMap, setBuddyInfoMap] = useState<Map<string, BuddyInfo>>(new Map())

  useEffect(() => {
    loadUserData()
  }, [])

  const loadUserData = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) { router.push('/login'); return }

      const decoded = parseAuthToken(token)
      if (!decoded) { router.push('/login'); return }

      const supabase = getSupabaseClient()

      let query = supabase.from('user_profiles').select('*')
      if (decoded.kakao_id) query = query.eq('kakao_id', decoded.kakao_id)
      else if (decoded.naver_id) query = query.eq('naver_id', decoded.naver_id)
      else { router.push('/login'); return }

      const { data: profile, error: profileError } = await query.single()
      if (profileError || !profile) {
        toast.error('프로필을 찾을 수 없습니다', '로그인을 다시 시도해주세요.')
        router.push('/login')
        return
      }

      setUserProfile({
        name: profile.name,
        profile_image: profile.profile_image,
        certification_level: profile.certification_level,
        is_premium: profile.is_premium
      })
      setCurrentUserId(profile.id)

      const { data: requests, error: requestsError } = await supabase
        .from('match_requests')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false })

      if (requestsError) {
        toast.error('예약 목록을 불러올 수 없습니다', '잠시 후 다시 시도해주세요.')
      } else {
        setMatchingRequests(requests || [])

        const completedMatchings = (requests || []).filter(r => r.status === 'completed')
        if (completedMatchings.length > 0) {
          const { data: reviews } = await supabase
            .from('buddy_reviews')
            .select('matching_id')
            .eq('reviewer_user_id', profile.id)
            .in('matching_id', completedMatchings.map(m => m.id))

          if (reviews) setReviewedMatchings(new Set(reviews.map(r => r.matching_id)))
        }

        const matchedUserIds = (requests || [])
          .filter(r => r.matched_user_id)
          .map(r => r.matched_user_id!)
          .filter((id, index, arr) => arr.indexOf(id) === index)

        if (matchedUserIds.length > 0) {
          const { data: buddyProfiles, error: buddyError } = await supabase
            .from('user_profiles')
            .select('id, name, profile_image')
            .in('id', matchedUserIds)

          if (!buddyError && buddyProfiles) {
            const newBuddyMap = new Map<string, BuddyInfo>()
            buddyProfiles.forEach(buddy => {
              newBuddyMap.set(buddy.id, { id: buddy.id, name: buddy.name, profile_image: buddy.profile_image, isWithdrawn: false })
            })
            matchedUserIds.forEach(userId => {
              if (!newBuddyMap.has(userId)) {
                newBuddyMap.set(userId, { id: userId, name: '탈퇴한 회원', isWithdrawn: true })
              }
            })
            setBuddyInfoMap(newBuddyMap)
          }
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error)
      toast.error('데이터 로드 중 오류 발생', '로그인을 다시 시도해주세요.')
      router.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelRequest = async (requestId: string) => {
    try {
      const supabase = getSupabaseClient()
      const { error } = await supabase
        .from('match_requests')
        .update({ status: 'cancelled' })
        .eq('id', requestId)

      if (error) throw error
      setMatchingRequests(prev =>
        prev.map(request => request.id === requestId ? { ...request, status: 'cancelled' } : request)
      )
    } catch (error) {
      console.error('Error cancelling request:', error)
      toast.error('요청 취소 실패', '잠시 후 다시 시도해주세요.')
    }
  }

  const handleCompleteMatching = async (matchingId: string) => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch('/api/matching/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ matching_id: matchingId })
      })

      const result = await response.json()
      if (result.success) {
        toast.success('매칭이 완료되었습니다', '버디를 평가해주세요!')
        await loadUserData()
      } else {
        toast.error('매칭 완료 실패', result.error || '잠시 후 다시 시도해주세요.')
      }
    } catch (error) {
      console.error('Error completing matching:', error)
      toast.error('매칭 완료 중 오류 발생', '잠시 후 다시 시도해주세요.')
    }
  }

  const handleOpenReview = async (matchingId: string, buddyId: string) => {
    try {
      const supabase = getSupabaseClient()
      const { data: buddy, error } = await supabase
        .from('user_profiles')
        .select('id, name')
        .eq('id', buddyId)
        .single()

      if (error || !buddy) { toast.error('버디 정보를 찾을 수 없습니다'); return }

      setSelectedMatching({ matchingId, buddyId: buddy.id, buddyName: buddy.name })
      setReviewModalOpen(true)
    } catch (error) {
      console.error('Error loading buddy info:', error)
      toast.error('버디 정보 로드 중 오류 발생')
    }
  }

  const handleSubmitReview = async (reviewData: ReviewData) => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch('/api/buddy-review/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(reviewData)
      })

      const result = await response.json()
      if (result.success) {
        toast.success('평가가 완료되었습니다', '감사합니다!')
        setReviewModalOpen(false)
        setSelectedMatching(null)
        await loadUserData()
      } else {
        toast.error('평가 제출 실패', result.error)
      }
    } catch (error) {
      console.error('Error submitting review:', error)
      toast.error('평가 제출 중 오류 발생', '잠시 후 다시 시도해주세요.')
    }
  }

  const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
    active: { label: '매칭 대기중', color: isDark ? '#60a5fa' : '#2563eb', bg: isDark ? '#1e3a5f' : '#dbeafe' },
    matched: { label: '매칭 완료', color: isDark ? '#4ade80' : '#16a34a', bg: isDark ? '#14532d' : '#dcfce7' },
    cancelled: { label: '취소됨', color: isDark ? '#f87171' : '#dc2626', bg: isDark ? '#450a0a' : '#fee2e2' },
    completed: { label: '완료됨', color: isDark ? '#94a3b8' : '#6b7280', bg: isDark ? '#1e2a3d' : '#f3f4f6' },
  }

  if (isLoading) {
    return (
      <div className="min-h-screen pb-24" style={{ background: bg }}>
        <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <CardSkeleton key={i} className={isDark ? 'bg-[#1e2a3d] border-[#2c3a52]' : 'bg-white border-[#e9eff7]'} />
          ))}
        </div>
      </div>
    )
  }

  const activeRequests = matchingRequests.filter(req => req.status === 'active')
  const matchedRequests = matchingRequests.filter(req => req.status === 'matched')
  const completedRequests = matchingRequests.filter(req => req.status === 'completed')

  return (
    <div className="min-h-screen pb-24" style={{ background: bg }}>
      {/* 헤더 */}
      <div className="sub-sticky z-20 border-b backdrop-blur-xl" style={{ background: isDark ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.95)', borderColor: border }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold tracking-tight" style={{ color: ink }}>내 예약</h1>
            <p className="text-xs" style={{ color: secondary }}>매칭 신청 현황</p>
          </div>
          <button onClick={loadUserData} className="p-2 rounded-lg" style={{ color: secondary }}>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">

        {/* 통계 카드 */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: '대기 중', value: activeRequests.length, color: isDark ? '#60a5fa' : '#2563eb' },
            { label: '매칭 완료', value: matchedRequests.length, color: isDark ? '#4ade80' : '#16a34a' },
            { label: '완료', value: completedRequests.length, color: secondary },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-2xl p-3 border" style={{ background: card, borderColor: border }}>
              <p className="text-2xl font-bold tabular-nums tracking-tight" style={{ color }}>{value}</p>
              <p className="text-xs mt-0.5" style={{ color: secondary }}>{label}</p>
            </div>
          ))}
        </div>

        {/* 매칭 요청 목록 */}
        {matchingRequests.length === 0 ? (
          <div className="rounded-2xl border py-12 text-center" style={{ background: card, borderColor: border }}>
            <Calendar className="w-10 h-10 mx-auto mb-3" style={{ color: secondary }} />
            <p className="text-sm font-medium mb-1" style={{ color: ink }}>아직 매칭 요청이 없습니다</p>
            <p className="text-xs mb-5" style={{ color: secondary }}>첫 번째 매칭 요청을 생성해보세요!</p>
            <button
              onClick={() => router.push('/matching/new')}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold"
              style={{ background: isDark ? '#1e3a5f' : '#0a1f3a' }}
            >
              매칭 요청 생성
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {matchingRequests.map((request) => {
              const sc = statusConfig[request.status] || statusConfig.cancelled
              const buddy = request.matched_user_id ? buddyInfoMap.get(request.matched_user_id) : null

              return (
                <div key={request.id} className="rounded-2xl border" style={{ background: card, borderColor: border }}>
                  {/* 상태 헤더 */}
                  <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: border }}>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ color: sc.color, background: sc.bg }}>
                      {sc.label}
                    </span>
                    <div className="flex items-center gap-2">
                      {request.status === 'matched' && (
                        <button
                          onClick={() => router.push(`/matching/${request.id}`)}
                          className="text-xs px-3 py-1 rounded-lg font-medium"
                          style={{ color: accent, background: isDark ? '#1e3a5f' : '#eff6ff' }}
                        >
                          버디 정보 보기
                        </button>
                      )}
                      <span className="text-xs" style={{ color: secondary }}>
                        {new Date(request.created_at).toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  </div>

                  <div className="px-4 py-3 space-y-3">
                    {/* 버디 정보 */}
                    {buddy && (
                      <div className="flex items-center gap-3 p-3 rounded-xl border" style={{
                        borderColor: buddy.isWithdrawn ? border : (isDark ? '#1e3a5f' : '#bfdbfe'),
                        background: buddy.isWithdrawn ? (isDark ? '#1e2a3d' : '#f8fafc') : (isDark ? '#0f2040' : '#eff6ff')
                      }}>
                        <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                          style={{ background: buddy.isWithdrawn ? (isDark ? '#2c3a52' : '#e5e7eb') : (isDark ? '#1e3a5f' : '#dbeafe') }}>
                          {buddy.profile_image && !buddy.isWithdrawn ? (
                            <img src={buddy.profile_image} alt={buddy.name} className="w-full h-full object-cover" />
                          ) : (
                            <Users className="w-4 h-4" style={{ color: buddy.isWithdrawn ? secondary : accent }} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs" style={{ color: secondary }}>매칭 버디</div>
                          <div className="text-sm font-medium truncate" style={{ color: buddy.isWithdrawn ? secondary : ink }}>
                            {buddy.name}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 매칭 정보 */}
                    <div className="grid grid-cols-1 gap-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: accent }} />
                        <span className="text-sm" style={{ color: ink }}>{request.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: accent }} />
                        <span className="text-sm" style={{ color: ink }}>{request.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: accent }} />
                        <span className="text-sm" style={{ color: ink }}>{request.diving_center}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
                      <span className="text-xs" style={{ color: secondary }}>
                        선호 레벨: {request.experience_level || '상관없음'}
                      </span>

                      <div className="flex gap-2">
                        {request.status === 'active' && (
                          <button
                            onClick={() => handleCancelRequest(request.id)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium"
                            style={{ background: isDark ? '#450a0a' : '#fee2e2', color: isDark ? '#f87171' : '#dc2626' }}
                          >
                            취소하기
                          </button>
                        )}

                        {request.status === 'matched' && request.matched_user_id && (
                          <button
                            onClick={() => handleCompleteMatching(request.id)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1"
                            style={{ background: isDark ? '#14532d' : '#dcfce7', color: isDark ? '#4ade80' : '#16a34a' }}
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            완료하기
                          </button>
                        )}

                        {request.status === 'completed' && request.matched_user_id && !reviewedMatchings.has(request.id) && (
                          <button
                            onClick={() => handleOpenReview(request.id, request.matched_user_id!)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1"
                            style={{ background: isDark ? '#1e3a5f' : '#0a1f3a', color: '#fff' }}
                          >
                            <Star className="w-3.5 h-3.5" />
                            평가하기
                          </button>
                        )}

                        {request.status === 'completed' && reviewedMatchings.has(request.id) && (
                          <div className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1"
                            style={{ background: isDark ? '#14532d' : '#dcfce7', color: isDark ? '#4ade80' : '#16a34a' }}>
                            <CheckCircle className="w-3.5 h-3.5" />
                            평가 완료
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* 새 매칭 요청 버튼 */}
        {matchingRequests.length > 0 && (
          <button
            onClick={() => router.push('/matching/new')}
            className="w-full py-3.5 rounded-2xl text-sm font-semibold"
            style={{ background: card, borderColor: border, color: accent, border: `1px solid ${border}` }}
          >
            + 새 매칭 요청 만들기
          </button>
        )}
      </div>

      {selectedMatching && (
        <BuddyReviewModal
          isOpen={reviewModalOpen}
          onClose={() => { setReviewModalOpen(false); setSelectedMatching(null) }}
          buddyName={selectedMatching.buddyName}
          buddyId={selectedMatching.buddyId}
          matchingId={selectedMatching.matchingId}
          onSubmit={handleSubmitReview}
        />
      )}
    </div>
  )
}
